// FILE: src/components/ErrorBoundary.js
//
// Top-level error boundary. Catches render-time errors anywhere in the tree,
// reports them to telemetry (technical health), and shows a minimal, calm
// fallback so a crash never leaves the learner on a blank white page.
//
// NOTE ON COMPONENT STYLE: the project standard is functional components, but
// React error boundaries REQUIRE a class — getDerivedStateFromError and
// componentDidCatch have no hook equivalents. This is the one necessary
// exception; everything else stays functional.

import React from 'react';
import { trackError, flushTelemetry } from '../../services/telemetryService';

const styles = {
  wrap: {
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#e8e5de',
    padding: '24px',
    boxSizing: 'border-box',
  },
  card: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    padding: '32px',
    maxWidth: '460px',
    textAlign: 'center',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  },
  title: { fontSize: '1.2rem', fontWeight: 700, color: '#222', marginBottom: '8px' },
  body:  { fontSize: '0.95rem', color: '#555', lineHeight: 1.6, marginBottom: '20px' },
  button: {
    padding: '10px 22px',
    background: '#1565c0',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 600,
  },
};

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Fire-and-forget telemetry. Never re-throws.
    try {
      trackError(error, { code: 'render', target: 'ErrorBoundary' });
      flushTelemetry();
    } catch (_) { /* swallow — telemetry must never worsen a crash */ }
    // Keep a console trace for local debugging.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught render error:', error, info);
  }

  handleReload = () => {
    try {
      if (typeof window !== 'undefined') window.location.reload();
    } catch (_) { /* no-op */ }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={styles.wrap} data-testid="error-boundary-fallback">
          <div style={styles.card}>
            <div style={styles.title}>Something went wrong</div>
            <div style={styles.body}>
              The page hit an unexpected error. Your progress is saved — reloading
              usually clears it up.
            </div>
            <button style={styles.button} onClick={this.handleReload}>
              Reload the app
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
